import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { ApiBaseUrls, ApiControllers } from "../../constants/constants";
import { ServiceResultModel } from "../../models/service/service-result.model";
import { ServiceUtilities } from "../../utilities/service.utilities";
import { APISanitizerUtilities } from "../../utilities/api-sanitizer.utilities";
import {
	PaymentPreviewModel,
	PaymentPreviewRequest,
	PaymentIntentModel,
	CreatePaymentIntentRequest,
	RefundPaymentRequest
} from "../../models/data/payment.model";
import { BalancesMessagingConstants } from "../../constants/messaging.constants";
import { PaymentHistoryModel } from "../../models/data/payment-history.model";
import { PaymentHistoryFilter } from "../../filters/payment-history.filter";
import { PaginatedListModel } from "../../models/service/paginated-list.model";
import { PaginationResponseHeader } from "../../models/service/pagination-response-header.model";
import { PaymentBulkActionRequest } from "../../models/service/payment-bulk-action-request.model";
import { PaymentBulkSummaryModel } from "../../models/data/payment-bulk-summary.model";
import { BulkActionResponse } from "../../models/service/bulk-action-response.model";

@Injectable({
	providedIn: 'root'
})
export class PaymentService {
	private paymentsApiPath: string = `${ApiBaseUrls.CORE()}${ApiControllers.PAYMENTS()}`;

	constructor(private httpClient: HttpClient) { }

	public async getPaymentPreview(request: PaymentPreviewRequest): Promise<ServiceResultModel<PaymentPreviewModel>> {
		try {
			const requestUrl = `${this.paymentsApiPath}/preview`;
			const response = await firstValueFrom(
				this.httpClient.post<PaymentPreviewModel>(
					requestUrl,
					JSON.stringify(APISanitizerUtilities.sanitizePayload(request)),
					{ observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
				)
			);

			const preview = <PaymentPreviewModel>response.body?.valueOf();
			return <ServiceResultModel>{ data: preview, success: true, statusCode: response.status };
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	public async createPaymentIntent(request: CreatePaymentIntentRequest): Promise<ServiceResultModel<PaymentIntentModel>> {
		try {
			const requestUrl = this.paymentsApiPath;
			const response = await firstValueFrom(
				this.httpClient.post<PaymentIntentModel>(
					requestUrl,
					JSON.stringify(APISanitizerUtilities.sanitizePayload(request)),
					{ observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
				)
			);

			const paymentIntent = <PaymentIntentModel>response.body?.valueOf();
			return <ServiceResultModel>{ data: paymentIntent, success: true, statusCode: response.status };
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	public async searchPaymentHistory(filter: PaymentHistoryFilter, orderBy?: string, skip?: number, take?: number): Promise<ServiceResultModel<PaginatedListModel<PaymentHistoryModel>>> {
		try {
			// Interpolated, so an omitted value has to become an empty string rather than reaching the
			// API as the literal "undefined", which it rejects with a 400 on the query string itself.
			const encodedOrderBy = orderBy ? encodeURIComponent(orderBy) : '';

			const requestUrl = this.paymentsApiPath
				+ `/filter?orderBy=${encodedOrderBy}&skip=${skip ?? 0}&take=${take ?? ''}`;

			const response = await firstValueFrom(
				this.httpClient.post<PaymentHistoryModel[]>(
					requestUrl,
					JSON.stringify(APISanitizerUtilities.sanitizePayload<PaymentHistoryFilter>(filter)),
					{ observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
				));

			const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
			const paginatedList: PaginatedListModel<PaymentHistoryModel> = {
				data: <PaymentHistoryModel[]>response.body?.valueOf(),
				total: paginationHeader?.total,
				skip: paginationHeader?.skip,
				take: paginationHeader?.take,
				hasPrevious: paginationHeader?.hasPrevious,
				hasNext: paginationHeader?.hasNext
			};

			return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	public async downloadReceipt(paymentId: number, isManualPayment: boolean = false): Promise<ServiceResultModel<Blob>> {
		try {
			const requestUrl = isManualPayment
				? `${this.paymentsApiPath}/manual/${paymentId}/receipt`
				: `${this.paymentsApiPath}/${paymentId}/receipt`;
			const response = await firstValueFrom(
				this.httpClient.get(
					requestUrl,
					{ observe: "response", responseType: 'blob' }
				)
			);

			const contentDisposition = response.headers.get('Content-Disposition');
			return <ServiceResultModel<Blob>>{
				data: response.body,
				success: true,
				statusCode: response.status,
				message: contentDisposition
			};
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	public async downloadInvoice(paymentId: number, isManualPayment: boolean = false): Promise<ServiceResultModel<Blob>> {
		try {
			const requestUrl = isManualPayment
				? `${this.paymentsApiPath}/manual/${paymentId}/invoice`
				: `${this.paymentsApiPath}/${paymentId}/invoice`;
			const response = await firstValueFrom(
				this.httpClient.get(
					requestUrl,
					{ observe: "response", responseType: 'blob' }
				)
			);

			const contentDisposition = response.headers.get('Content-Disposition');
			return <ServiceResultModel<Blob>>{
				data: response.body,
				success: true,
				statusCode: response.status,
				message: contentDisposition
			};
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	public async resendReceipt(paymentId: number, isManualPayment: boolean = false): Promise<ServiceResultModel> {
		try {
			const requestUrl = isManualPayment
				? `${this.paymentsApiPath}/manual/${paymentId}/receipt/resend`
				: `${this.paymentsApiPath}/${paymentId}/receipt/resend`;
			const response = await firstValueFrom(
				this.httpClient.post(
					requestUrl,
					null,
					{ observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
				)
			);

			return <ServiceResultModel>{ success: true, statusCode: response.status };
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	public async resendRefundStatement(paymentId: number): Promise<ServiceResultModel> {
		try {
			const requestUrl = `${this.paymentsApiPath}/${paymentId}/refund/statement/resend`;
			const response = await firstValueFrom(
				this.httpClient.post(
					requestUrl,
					null,
					{ observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
				)
			);

			return <ServiceResultModel>{ success: true, statusCode: response.status };
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	public async getPaymentBulkSummary(request: PaymentBulkActionRequest): Promise<ServiceResultModel<PaymentBulkSummaryModel>> {
		try {
			const requestUrl = `${this.paymentsApiPath}/bulk/summary`;
			const response = await firstValueFrom(
				this.httpClient.post<PaymentBulkSummaryModel>(
					requestUrl,
					JSON.stringify(APISanitizerUtilities.sanitizePayload<PaymentBulkActionRequest>(request)),
					{ observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
				)
			);

			const summary = <PaymentBulkSummaryModel>response.body?.valueOf();
			return <ServiceResultModel>{ data: summary, success: true, statusCode: response.status };
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	public async refundPayment(paymentId: number, request: RefundPaymentRequest): Promise<ServiceResultModel<PaymentIntentModel>> {
		try {
			const requestUrl = `${this.paymentsApiPath}/${paymentId}/refund`;
			const response = await firstValueFrom(
				this.httpClient.post<PaymentIntentModel>(
					requestUrl,
					JSON.stringify(APISanitizerUtilities.sanitizePayload(request)),
					{ observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
				)
			);

			const paymentIntent = <PaymentIntentModel>response.body?.valueOf();
			return <ServiceResultModel>{ data: paymentIntent, success: true, statusCode: response.status };
		} catch (error) {
			const messageOverride = this.resolveRefundErrorMessage(error);
			return ServiceUtilities.handleServiceException(error, messageOverride);
		}
	}

	public async cancelRefund(paymentId: number): Promise<ServiceResultModel<PaymentIntentModel>> {
		try {
			const requestUrl = `${this.paymentsApiPath}/${paymentId}/refund/cancel`;
			const response = await firstValueFrom(
				this.httpClient.post<PaymentIntentModel>(
					requestUrl,
					null,
					{ observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
				)
			);

			const paymentIntent = <PaymentIntentModel>response.body?.valueOf();
			return <ServiceResultModel>{ data: paymentIntent, success: true, statusCode: response.status };
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	// Requests cancelation of a Pending ACH payment; on success the payment comes back as Canceling.
	// No message override: the API's detail (not pending, card payment, already submitted to the
	// bank) is the copy the user should see.
	public async cancelPayment(paymentId: number): Promise<ServiceResultModel<PaymentIntentModel>> {
		try {
			const requestUrl = `${this.paymentsApiPath}/${paymentId}/cancel`;
			const response = await firstValueFrom(
				this.httpClient.post<PaymentIntentModel>(
					requestUrl,
					null,
					{ observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
				)
			);

			const paymentIntent = <PaymentIntentModel>response.body?.valueOf();
			return <ServiceResultModel>{ data: paymentIntent, success: true, statusCode: response.status };
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	public async downloadRefundStatement(paymentId: number): Promise<ServiceResultModel<Blob>> {
		try {
			const requestUrl = `${this.paymentsApiPath}/${paymentId}/refund/statement`;
			const response = await firstValueFrom(
				this.httpClient.get(
					requestUrl,
					{ observe: "response", responseType: 'blob' }
				)
			);

			const contentDisposition = response.headers.get('Content-Disposition');
			return <ServiceResultModel<Blob>>{
				data: response.body,
				success: true,
				statusCode: response.status,
				message: contentDisposition
			};
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	// 422 from POST /payments/{id}/refund covers several cases (insufficient Stripe balance,
	// payment not refundable, invalid payable account). Until the API exposes a discriminator
	// (e.g. an error code or problem-detail `type`), we match on "insufficient" — the most
	// distinguishing word for the balance case — to surface the spec copy. Other 422 reasons
	// fall through to the API's own detail message.
	// TODO: Ask Jake about details, Replace with a structured error code once backend adds one.
	private resolveRefundErrorMessage(error: any): string | undefined {
		if (error?.error?.statusCode !== 422) return undefined;
		const detail: string = error?.error?.detail ?? '';
		if (detail.toLowerCase().includes('insufficient')) {
			return BalancesMessagingConstants.REFUND_PAYMENT_INSUFFICIENT_FUNDS();
		}
		return undefined;
	}

	public async bulkResendReceipts(request: PaymentBulkActionRequest): Promise<ServiceResultModel<BulkActionResponse>> {
		try {
			const requestUrl = `${this.paymentsApiPath}/bulk/receipt/resend`;
			const response = await firstValueFrom(
				this.httpClient.post<BulkActionResponse>(
					requestUrl,
					JSON.stringify(APISanitizerUtilities.sanitizePayload<PaymentBulkActionRequest>(request)),
					{ observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
				)
			);

			const result = <BulkActionResponse>response.body?.valueOf();
			return <ServiceResultModel>{
				data: result,
				success: true,
				message: BalancesMessagingConstants.BULK_RESEND_RECEIPTS_SUCCESS()
					.replace('{totalAffected}', String(result?.totalAffected ?? 0))
					.replace('{totalEvaluated}', String(result?.totalEvaluated ?? 0))
					.replace('{totalSkipped}', String(result?.totalSkipped ?? 0)),
				statusCode: response.status
			};
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	public async exportPaymentsCsv(request: PaymentBulkActionRequest): Promise<ServiceResultModel<Blob>> {
		try {
			const requestUrl = `${this.paymentsApiPath}/bulk/export`;
			const response = await firstValueFrom(
				this.httpClient.post(
					requestUrl,
					JSON.stringify(APISanitizerUtilities.sanitizePayload<PaymentBulkActionRequest>(request)),
					{ observe: "response", responseType: 'blob', headers: { 'Content-Type': 'application/json' } }
				)
			);

			return <ServiceResultModel<Blob>>{ data: response.body, success: true, statusCode: response.status };
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}
}
